// es.ts — Spanish catalog. `satisfies Catalog` is the whole safety net: a missing key, a
// misspelled key, or a function with the wrong params is a `check-types` failure.
//
// Translated per docs/superpowers/specs/locale-translator-contract.md (binding terminology,
// do-not-translate list, tone rules — imported by reference from locale-glossary-vi.md §2).
// Register: "tú" (informal, second person singular) throughout, the standard modern-SaaS
// choice for Spanish dashboard UI — direct, technical, no marketing-agency fluff.
import type { Catalog } from './en';

export const es = {
  common: {
    close: 'Cerrar',
  },
  status: {
    pending: 'pendiente',
    crawling: 'rastreando',
    indexing: 'indexando',
    ready: 'listo',
    error: 'error',
    paused: 'pausado',
    hint: 'aviso',
    deleting: 'eliminando',
  },
  time: {
    never: 'nunca',
    justNow: 'justo ahora',
    inAMoment: 'en un momento',
    secondsAgo: ({ n }: { n: number }) => `hace ${n}s`,
    inSeconds: ({ n }: { n: number }) => `en ${n}s`,
    minutesAgo: ({ n }: { n: number }) => `hace ${n} min`,
    inMinutes: ({ n }: { n: number }) => `en ${n} min`,
    hoursAgo: ({ n }: { n: number }) => `hace ${n}h`,
    inHours: ({ n }: { n: number }) => `en ${n}h`,
    daysAgo: ({ n }: { n: number }) => `hace ${n}d`,
    inDays: ({ n }: { n: number }) => `en ${n}d`,
    monthsAgo: ({ n }: { n: number }) => `hace ${n}m`,
    inMonths: ({ n }: { n: number }) => `en ${n}m`,
    yearsAgo: ({ n }: { n: number }) => `hace ${n}a`,
    inYears: ({ n }: { n: number }) => `en ${n}a`,
  },
  shell: {
    impersonating: ({ email }: { email: string }) =>
      `Viendo como ${email} — estás suplantando a este usuario.`,
    stopImpersonating: 'Detener',

    platformAdmin: 'Admin de la plataforma',
    adminTabWorkspaces: 'Workspaces',
    adminTabBots: 'Bots',
    adminTabJobs: 'Jobs',
    adminTabPosts: 'Entradas',
    adminTabFeedback: 'Feedback',
    adminTabPlayground: 'Playground',

    botsLoadError: 'No se pudieron cargar tus bots.',
    botsLoadErrorInline: 'No se pudieron cargar tus bots',
    reloadToRetry: 'Recarga la página para volver a intentarlo.',

    navOverview: 'Resumen',
    navUnanswered: 'Sin responder',
    navConversations: 'Conversaciones',
    navLeads: 'Leads',
    navSources: 'Fuentes',
    navPlayground: 'Playground',
    navAppearance: 'Apariencia',
    navSettings: 'Configuración',
    groupNeedsYou: 'TE NECESITA',
    groupKnowledge: 'CONOCIMIENTO',
    groupSetup: 'CONFIGURACIÓN',

    closeMenu: 'Cerrar menú',
    navigationLabel: 'Navegación',
    openNavigation: 'Abrir navegación',
    openAccountMenu: 'Abrir menú de cuenta',

    switchBotAriaLabel: ({ name }: { name: string }) => `${name}, cambiar de bot`,
    botSwitcherHeader: ({ n, cap }: { n: number; cap: number | null }) =>
      `TUS BOTS${cap !== null ? ` · ${n} DE ${cap} USADOS` : ''}`,
    allBots: 'Todos los bots',
    newBot: 'Nuevo bot',
    newBotOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `Nuevo bot — ${n} de ${cap} usados`,

    pagesIndexed: 'Páginas indexadas',

    feedbackTrigger: 'Feedback',
    feedbackTypeGroupLabel: 'Tipo de feedback',
    feedbackTypeBug: 'Error',
    feedbackTypeFeature: 'Solicitud de función',
    feedbackTypeQuestion: 'Pregunta',
    feedbackPlaceholder: '¿Qué pasó, o qué haría que Embent fuera mejor para ti?',
    feedbackSent: 'Enviado — leemos todos.',
    feedbackSendError: 'No se pudo enviar el feedback.',
    feedbackSending: 'Enviando…',
    feedbackSend: 'Enviar',
    feedbackDialogTitle: 'Enviar feedback',
    feedbackDialogDescription: 'Error, solicitud de función, o pregunta — esto llega directo al equipo.',

    workspaceSettings: 'Configuración del workspace',
    members: 'Miembros',
    billingUsage: 'Facturación y uso',
    switchWorkspace: 'Cambiar de workspace',
    accountLabelPrefix: 'Cuenta — ',
    account: 'Cuenta',
    signOut: 'Cerrar sesión',
    themePrefix: 'Tema: ',
    languagePrefix: 'Idioma: ',
    nextPrefix: 'siguiente: ',
    themeSystem: 'Sistema',
    themeDark: 'Oscuro',
    themeLight: 'Claro',
    roleOwner: 'Propietario',
    roleAdmin: 'Admin',
    roleMember: 'Miembro',
    workspaceSubline: ({ plan, role }: { plan: string | null; role: string }) =>
      plan ? `${plan} · eres ${role}` : `Eres ${role}`,
    workspaceMenuAriaLabel: ({ name }: { name: string }) => `${name}, menú de workspace y cuenta`,

    usageWarning: ({ pct }: { pct: number }) =>
      `Has usado el ${pct}% de tus mensajes mensuales — actualiza tu plan para que ninguna conversación se corte.`,
    dismiss: 'Descartar',

    renewsOn: ({ date }: { date: string }) => `Se renueva el ${date}`,
    resetsOn: ({ date }: { date: string }) => `Se reinicia el ${date}`,
    answersPaused: 'Respuestas nuevas pausadas — por encima del 110% del límite',
    messagesLeft: ({ n }: { n: string }) => `${n} mensajes restantes este mes`,
    badgeStillShown: ({ plan }: { plan: string }) => `${plan} sigue mostrando la insignia "Powered by Embent"`,
    badgeOptional: ({ plan }: { plan: string }) => `${plan} hace opcional la insignia "Powered by Embent"`,
    upgradeCta: ({ price }: { price: number }) => `Actualizar — $${price}/mes`,
    resumeAnswersCta: ({ price }: { price: number }) => `Reanudar respuestas — $${price}/mes`,
    resumeAnswersBenefit: 'Actualizar tu plan reanuda las respuestas de inmediato',
    upgradeBenefit: ({
      plan,
      messages,
      bots,
      seats,
    }: {
      plan: string;
      messages: string;
      bots: number;
      seats: number;
    }) => `${plan} añade ${messages} mensajes, ${bots} bots y ${seats} ${seats === 1 ? 'usuario' : 'usuarios'}`,
  },
  bots: {
    noSources: 'sin fuentes',
    allSourcesReady: ({ n }: { n: number }) => `${n} · todas listas`,
    sourceStatusRollup: ({ n, status }: { n: number; status: string }) => `${n} · ${status}`,
    paused: 'Pausado',
    sourcesRemoving: ({ n }: { n: number }) => `${n} ${n === 1 ? 'fuente' : 'fuentes'} · eliminando`,
    sourcesSyncError: ({ n }: { n: number }) => `${n} ${n === 1 ? 'fuente' : 'fuentes'} · error de sincronización`,
    sourcesSyncing: ({ n }: { n: number }) => `${n} ${n === 1 ? 'fuente' : 'fuentes'} · sincronizando`,
    messagesUnanswered: ({ messages, unanswered }: { messages: number; unanswered: number }) =>
      `${messages} ${messages === 1 ? 'mensaje' : 'mensajes'} · ${unanswered} sin responder`,
    botsCount: ({ n }: { n: number }) => `${n} ${n === 1 ? 'bot' : 'bots'}`,
    botsOfCapUsed: ({ n, cap }: { n: number; cap: number }) => `${n} de ${cap} usados`,

    colBot: 'Bot',
    colStatus: 'Estado',
    colMessages14d: 'Mensajes · 14d',
    colUnanswered: 'Sin responder',
    colSources: 'Fuentes',
    pageTitle: 'Bots',
    emptyTitle: 'Crea tu primer bot',
    emptyDescription:
      'Apúntalo a tu web o documentación — rastrea el contenido y empieza a responder preguntas de visitantes.',
    planIncludesBots: ({ cap }: { cap: number }) => `Tu plan incluye ${cap} ${cap === 1 ? 'bot' : 'bots'}`,
    upgradeGateDescription: ({ name, priceUsd, maxBots }: { name: string; priceUsd: number; maxBots: number }) =>
      `${name} ($${priceUsd}/mes) permite hasta ${maxBots} bots — uno por sitio, cada uno con su propio conocimiento y apariencia.`,
    comparePlansLink: 'Comparar planes →',
    newBotDialogTitle: 'Nuevo bot',
    botNameFieldLabel: 'Nombre',
    botNamePlaceholder: 'Bot de soporte de Acme',
    creatingCta: 'Creando…',
    createCta: 'Crear',
  },
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Contraseña',
    genericFormError: 'Algo salió mal. Vuelve a intentarlo.',
    continueWithGoogle: 'Continuar con Google',
    backToSignIn: 'Volver a iniciar sesión',

    // SignIn.tsx
    signInTitle: 'Iniciar sesión',
    signInSubtitle: 'Bienvenido de nuevo a Embent.',
    passwordJustResetNotice: 'Contraseña actualizada — inicia sesión con tu nueva contraseña.',
    forgotPasswordLink: '¿Olvidaste tu contraseña?',
    unverifiedResendNotice: 'Revisa tu bandeja de entrada — te reenviamos el email de verificación.',
    signingIn: 'Iniciando sesión…',
    signInCta: 'Iniciar sesión',
    noAccountPrompt: '¿No tienes cuenta? ',
    signUpLink: 'Regístrate',

    // SignUp.tsx
    signUpTitle: 'Crea tu cuenta',
    signUpSubtitle: 'Empieza a responder preguntas de visitantes en minutos.',
    nameLabel: 'Nombre',
    creatingAccount: 'Creando cuenta…',
    createAccountCta: 'Crear cuenta',
    haveAccountPrompt: '¿Ya tienes cuenta? ',
    signInLink: 'Iniciar sesión',

    // CheckEmail.tsx
    checkEmailTitle: 'Revisa tu email',
    checkEmailSubtitle: 'Te enviamos un enlace de verificación. Suele llegar en menos de un minuto.',
    checkEmailBody: 'Haz clic en el enlace del email para activar tu cuenta.',

    // VerifyEmail.tsx
    verifyFailedTitle: 'La verificación falló',
    verifiedTitle: 'Email verificado',
    verifyFailedBody: 'Ese enlace de verificación no es válido o ha caducado.',
    verifiedBody: 'Todo listo — te estamos llevando a tu dashboard…',

    // RequestReset.tsx
    requestResetTitle: 'Restablece tu contraseña',
    requestResetSubtitlePending: 'Ingresa tu email y te enviaremos un enlace para restablecerla.',
    requestResetSubtitleSent: 'Si ese email tiene una cuenta de Embent, te enviamos un enlace para restablecerla.',
    requestResetBodySent: 'Revisa tu bandeja de entrada. El enlace caduca en 1 hora.',
    sendingResetLink: 'Enviando…',
    sendResetLinkCta: 'Enviar enlace de restablecimiento',

    // ResetPassword.tsx
    resetLinkInvalidTitle: 'Enlace de restablecimiento no válido',
    resetLinkInvalidBody: 'A este enlace de restablecimiento de contraseña le falta su token, o ya se usó.',
    requestNewLinkCta: 'Solicitar un enlace nuevo',
    chooseNewPasswordTitle: 'Elige una nueva contraseña',
    chooseNewPasswordSubtitle: 'Que sea una que no hayas usado antes.',
    newPasswordLabel: 'Nueva contraseña',
    resetLinkExpiredFallback: 'Ese enlace de restablecimiento no es válido o ha caducado.',
    savingPassword: 'Guardando…',
    saveNewPasswordCta: 'Guardar nueva contraseña',

    // InviteAccept.tsx
    inviteNotValidTitle: 'Invitación no válida',
    goToEmbentLink: 'Ir a Embent',
    inviteInvalidFallback: 'Esta invitación ya no es válida.',
    inviteTitle: ({ workspaceName }: { workspaceName: string }) => `Estás invitado a ${workspaceName}`,
    inviteDescription: ({ email }: { email: string }) =>
      `Enviada a ${email} — inicia sesión o crea una cuenta con ese email para aceptar.`,
    inviteSignInCta: 'Iniciar sesión',
    inviteSignUpCta: 'Crear una cuenta',
    wrongAccountTitle: 'Cuenta incorrecta',
    signOutCta: 'Cerrar sesión',
    inviteAcceptFallback: 'Algo salió mal al aceptar esta invitación.',
    joiningWorkspaceTitle: ({ workspaceName }: { workspaceName: string }) => `Uniéndote a ${workspaceName}…`,
    joiningWorkspaceBody: 'Un momento.',

    eventLabels: {
      newLead: 'nuevo lead',
      unansweredDigest: 'resumen de sin responder',
      crawlFailed: 'rastreo fallido',
      weeklySummary: 'resumen semanal',
    },
    unsubscribeLinkExpiredTitle: 'Este enlace ha caducado',
    unsubscribeLinkExpiredBody: 'Administra las notificaciones desde la configuración del bot en el dashboard.',
    openDashboardCta: 'Abrir dashboard',
    unsubscribedTitle: 'Te has dado de baja',
    unsubscribedBody: ({ eventLabel, botName }: { eventLabel: string; botName: string | null }) =>
      `Ya no recibirás emails de ${eventLabel} para ${botName ?? 'este bot'}. Puedes volver a activarlos en la configuración del bot.`,

    // NotFound.tsx
    notFoundEyebrow: 'Página no encontrada',
    notFoundTitle: 'No hay nada en esta dirección',
    notFoundBody: 'El enlace puede estar desactualizado, o la página pudo haberse movido.',
    notFoundCta: 'Ir a tus bots',

    // LandingResolver.tsx
    landingBotsErrorFallback: 'Algo salió mal cargando tus bots.',
    landingGoToBotsCta: 'Ir a tus bots →',
  },
  onboarding: {
    skipForNow: 'Omitir por ahora',
    invalidWebsiteUrl: 'Ingresa una URL de sitio web válida, por ejemplo https://acme.vn',
    websiteUrlLabel: 'URL del sitio web',
    genericFallbackError: 'Algo salió mal.',

    // Onboarding.tsx
    crawlDefaultHostname: 'tu sitio',
    noSourceToReplace: 'Todavía no hay ninguna fuente que reemplazar.',

    // Stepper.tsx
    stepWebsite: 'Sitio web',
    stepCrawl: 'Rastreo',
    stepTryIt: 'Pruébalo',
    stepLook: 'Aspecto',
    stepGoLive: 'Publicar',
    stepMobileReadout: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Paso ${current} de ${total} · ${label}`,
    stepProgressAriaLabel: ({
      current,
      total,
      label,
    }: {
      current: number;
      total: number;
      label: string;
    }) => `Progreso de onboarding: paso ${current} de ${total}, ${label}`,
    stepTablistAriaLabel: 'Progreso de onboarding',

    // WebsiteStep.tsx
    websiteHeading: 'Añade tu sitio web',
    websiteSubtitle:
      'Embent lee tu sitio y crea un bot que responde con citas — nada inventado. La mayoría de los sitios están listos en pocos minutos.',
    botNameLabel: 'Nombre del bot',
    botNamePlaceholder: 'Soporte de Acme',
    websiteUrlPlaceholder: 'acme.vn',
    websiteUrlHelper: 'Encontraremos tus páginas automáticamente. Puedes añadir más fuentes después.',
    botNameRequired: 'Ingresa un nombre para tu bot.',
    websiteStarting: 'Empezando…',
    websiteSubmitCta: 'Empezar a leer mi sitio',

    // CrawlStep.tsx
    crawlHeading: ({ hostname }: { hostname: string }) => `Leyendo ${hostname}…`,
    crawlSubtitleReady: 'Todo listo — cada página está indexada y lista para responder.',
    crawlSubtitleSyncing: 'No tienes que esperar aquí — prueba tu bot mientras termina.',
    crawlStatusLines: {
      pending: ['En cola — empezando en breve…', 'Preparándose para empezar…'],
      crawling: ['Buscando páginas en el sitio…', 'Siguiendo enlaces…', 'Leyendo páginas…'],
      indexing: ['Construyendo el índice de búsqueda…', 'Procesando lo que se leyó…'],
    },
    crawlPagesIndexed: ({ n }: { n: number }) => `${n} páginas indexadas`,
    crawlNoSourceYet: 'Todavía no encontramos una fuente para este bot.',
    crawlErrorLead:
      'La causa más probable es un error de escritura en la dirección — revísala abajo y vuelve a intentarlo.',
    crawlReplaceUrlCta: 'Probar otra dirección',
    crawlReplaceUrlUpdating: 'Actualizando…',
    crawlReplaceUrlHelper: 'Eliminaremos el intento fallido y empezaremos de nuevo con esta dirección.',
    crawlRetryCta: 'Reintentar',
    crawlRetrying: 'Reintentando…',
    crawlRetryHelper:
      'Si la dirección es correcta y el sitio simplemente estuvo inaccesible por un momento, mejor reinténtalo. Los ' +
      'sitios que necesitan JavaScript para mostrar su contenido se pueden manejar con un ajuste disponible después ' +
      'de la configuración inicial, en la pantalla de Fuentes.',
    crawlCtaSyncing: 'Prueba tu bot mientras termina →',
    crawlCtaReady: 'Siguiente: prueba tu bot →',

    // TryItStep.tsx
    tryItHeading: 'Pregúntale lo que sea',
    tryItSubtitleWithPages: ({ n }: { n: number }) =>
      `Ya conoce ${n} ${n === 1 ? 'página' : 'páginas'} — y todavía está aprendiendo el resto. Cada respuesta muestra sus fuentes.`,
    tryItSubtitleNoPages:
      'Todavía está leyendo tu sitio — prueba una pregunta de todos modos; las respuestas solo van a mejorar desde aquí.',
    tryItSamplePrompt: `Prueba: "¿Cuál es su política de devoluciones?"`,
    tryItPlaceholder: 'Pregunta sobre envíos, precios, lo que sea…',
    tryItSending: 'Enviando…',
    tryItSendCta: 'Enviar',
    tryItNextCta: 'Se ve bien — hazlo tuyo',

    // LookStep.tsx
    lookHeading: 'Hazlo a juego con tu sitio',
    lookSubtitle:
      'Elige tu color de marca — la vista previa se actualiza mientras escribes. Puedes ajustar todo con más detalle después en Apariencia.',
    brandColorLabel: 'Color de marca',
    colorHexAriaLabel: 'Valor hexadecimal del color',
    welcomeMessageLabel: 'Mensaje de bienvenida',
    lookSaving: 'Guardando…',
    lookContinueCta: 'Continuar',

    // GoLiveStep.tsx
    goLiveHeading: 'Un código y ya está en vivo',
    goLivePasteBeforePrefix: 'Pega esto antes de la etiqueta de cierre',
    goLivePasteBeforeSuffix: 'en tu sitio. Eso es todo — el bot aparece abajo a la derecha.',
    goLiveInstallGuidesLink: 'Guías de instalación por plataforma',
    goLiveFirstAnswerNotice: 'Te enviaremos un email en el momento en que el widget responda a su primer visitante.',
    goLiveDashboardCta: 'Ir al dashboard',
    goLiveViewSourcesCta: 'Ver fuentes',
  },
  overview: {
    title: 'Resumen',
    liveOnPrefix: 'En vivo en ',
    answeringSince: ({ date }: { date: string }) => `respondiendo desde ${date}`,
    testInPlaygroundCta: 'Probar en Playground',
    addSourcesCta: 'Añadir fuentes',
    botDetailsLoadError: ({ message }: { message: string }) => `No se pudieron cargar los detalles del bot — ${message}`,

    messagesLabel: 'Mensajes · últimos 14 días',
    conversationsLink: 'Conversaciones →',
    vsPreviousPeriod: 'vs. 14 días anteriores',
    leadsLabel: 'Leads capturados',
    newLeadsCount: ({ n }: { n: number }) => `${n} nuevos`,
    leadsThisWeek: ({ n }: { n: number }) => `${n} esta semana`,
    viewLeadsLink: 'Ver leads →',
    unansweredLabel: 'Preguntas sin responder',
    fixThemLink: 'Resolverlas →',

    knowledgeHeading: 'Conocimiento',
    pointAtSite: 'Apúntalo a tu sitio',
    addSourceLink: 'Añadir una fuente →',
    addSourcesLink: 'Añadir fuentes →',
    pagesIndexed: 'Páginas indexadas',
    sourcesCountLabel: 'Fuentes',
    lastSyncedLabel: 'Última sincronización',
    manageSourcesLink: 'Administrar fuentes →',
    resyncing: 'Resincronizando…',
    allSyncing: 'Todo sincronizando',
    resyncCta: 'Resincronizar',
    resyncNothingToSync: 'Nada que sincronizar — todas las fuentes ya se están sincronizando.',
    resyncGenericError: 'No se pudo iniciar una resincronización — vuelve a intentarlo.',

    latestConversationsHeading: 'Conversaciones recientes',
    allConversationsLink: 'Todas las conversaciones →',
    noConversationsEmpty: 'Sin actividad en los últimos 14 días — las conversaciones nuevas aparecerán aquí.',
    noMessageFallback: '(sin mensaje)',
    leadCapturedTag: 'Lead capturado',
    hasUnansweredTag: 'Tiene sin responder',

    knowledgeAllReady: 'Todo listo',
    knowledgeSyncing: 'Fuentes sincronizando',
    knowledgeNeedsAttention: 'Una fuente necesita atención',
    knowledgeRemoving: 'Eliminando fuentes',

    // FirstRun.tsx
    createdSubtitle: ({ time }: { time: string }) => `Creado ${time}`,
    zeroStatMessagesHint: 'Empieza con la primera pregunta',
    zeroStatLeadsHint: 'Nombre y email, en plena conversación',
    zeroStatUnansweredHint: 'Nada que resolver todavía',
    heroReadyHeadline: 'Tu bot está listo para responder',
    heroIndexingHeadline: 'Todavía leyendo tu sitio',
    heroEmptyHeadline: 'Añade una fuente para empezar',
    heroEmptyBody:
      'Todavía no tiene nada que leer. Añade una fuente y estará listo para responder en minutos.',
    heroIndexingErrorBody:
      'Una fuente necesita atención — no va a terminar por sí sola. Revisa la tarjeta de Conocimiento para ver qué está mal, y luego ponlo en tu sitio una vez que esté resuelto.',
    heroIndexingPrefix: 'Todavía está leyendo',
    heroIndexingNoSourceFallback: 'tu fuente',
    heroIndexingSuffix: ' — vuelve a revisar en unos minutos, y luego ponlo en tu sitio.',
    heroReadyBodyPrefix: ({ n }: { n: number }) => `Leyó ${n} ${n === 1 ? 'página' : 'páginas'}`,
    heroReadyFromDomain: ' de ',
    heroReadyBodySuffix:
      ' y las conoce lo suficiente como para responder preguntas sobre ellas. Falta un paso: ponlo en tu sitio.',
    onlyPagesNudge: ({ n }: { n: number }) =>
      `¿Solo ${n} ${n === 1 ? 'página' : 'páginas'}? Añade tus páginas de FAQ, precios o políticas para que tenga más con qué trabajar.`,
    addSourceCta: 'Añadir una fuente',
    addSourceHelperText:
      'Toma alrededor de un minuto. El código de inserción te estará esperando aquí mismo en cuanto tenga algo con qué responder.',
    pasteBeforeBodyLabel: 'PEGA ESTO ANTES DE </BODY>',
    emailSnippetLink: 'Enviar el código por email a mi desarrollador',
    emailSnippetSubject: ({ botName }: { botName: string }) => `Insertar ${botName} en nuestro sitio`,
    emailSnippetBody: ({ snippet }: { snippet: string }) => `Pega esto antes de la etiqueta de cierre </body>:\n\n${snippet}`,
    whatHappensNextHeading: 'Qué pasa después',
    stepVisitorAsksTitle: 'Un visitante pregunta algo',
    stepVisitorAsksBody: 'La burbuja está abajo a la derecha en cada página donde esté el código. No hay nada más que configurar.',
    stepEmailAnswerTitle: 'Te enviamos por email la primera respuesta',
    stepEmailAnswerBody: 'Así lees exactamente lo que dijo, con las palabras de tu cliente, antes que nadie más.',
    stepUnansweredTitle: 'Lo que no pudo responder llega aquí',
    stepUnansweredBody: 'Se niega a inventar. Cada rechazo se convierte en una línea que puedes resolver.',
    stepAddSourceTitle: 'Añade una fuente',
    stepAddSourceBody:
      'Apúntalo a tu sitio, o sube archivos y texto pegado — necesita algo que leer antes de poder responder.',
    stepAddSourceVisitorTitle: 'Un visitante pregunta, y te enviamos la respuesta por email',
    countersHeading: 'LOS CONTADORES EMPIEZAN CON TU PRIMER VISITANTE',

    // NeedsYou.tsx
    needsYouHeading: 'Te necesita',
    allUnansweredLink: 'Todo lo sin responder →',
    needsYouEmpty: 'Nada que resolver — tu bot respondió todo lo que le preguntaron.',
    needsYouIntro: 'Tu bot dijo que no lo sabía. Respóndelo una vez y lo sabrá para siempre.',
    askedSummary: ({ n, time }: { n: number; time: string }) =>
      `${n === 1 ? 'Preguntado una vez' : `Preguntado ${n} veces`} · última ${time}`,
    writeAnswerCta: 'Escribir respuesta',
    cancelCta: 'Cancelar',
    answerPlaceholder: 'Escribe la respuesta que le darías a este visitante. El bot la usará de ahora en adelante.',
    addingCta: 'Añadiendo…',
    addAsQaSourceCta: 'Añadir como fuente de P&R',
    saveAnswerError: 'No se pudo guardar esa respuesta — vuelve a intentarlo.',
  },
  sources: {
    pageTitle: 'Fuentes',
    sourcesSubtitle: ({
      n,
      pageCount,
      lastSynced,
    }: {
      n: number;
      pageCount: number;
      lastSynced: string;
    }) =>
      `${n} ${n === 1 ? 'fuente' : 'fuentes'} · ${pageCount} ${pageCount === 1 ? 'página' : 'páginas'} · última sincronización ${lastSynced}`,
    botLoadError: ({ message }: { message: string }) => `No se pudo cargar este bot — ${message}`,

    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Texto',
    typeFile: 'Archivo',

    colType: 'Tipo',
    colUri: 'URI',
    colStatus: 'Estado',
    colRenderedMode: 'Modo con renderizado',
    colPages: 'Páginas',
    colLastSynced: 'Última sincronización',
    colActions: 'Acciones',

    urlFieldLabel: 'URL de la página',
    urlPlaceholder: 'https://example.com/docs',
    sitemapFieldLabel: 'URL del sitemap',
    sitemapPlaceholder: 'https://example.com/sitemap.xml',
    textTitleFieldLabel: 'Título',
    textTitlePlaceholder: 'Política de reembolsos',
    textContentFieldLabel: 'Contenido',
    textContentPlaceholder: 'Pega el texto que quieres que el bot conozca…',
    fileFieldLabel: 'Archivo',
    fileDropPrompt: 'Suelta un archivo aquí, o haz clic para buscarlo',
    fileDropCaption: ({ extensions, maxSize }: { extensions: string; maxSize: string }) =>
      `${extensions} · hasta ${maxSize}`,
    fileTooLarge: ({ maxSize }: { maxSize: string }) => `Ese archivo supera el límite de ${maxSize}.`,
    fileTypeNotSupported: ({ ext, extensions }: { ext: string; extensions: string }) =>
      `${ext ? `.${ext}` : 'Ese archivo'} no es compatible — usa ${extensions}.`,
    filesDroppedNote: ({ name, count }: { name: string; count: number }) =>
      `Usando "${name}" — ${count} ${count === 1 ? 'otro archivo soltado, ignorado' : 'otros archivos soltados, ignorados'}.`,
    fileDropNotAFile: 'Eso no era un archivo — arrastra un archivo desde tu computadora en su lugar.',
    fileTitleFieldLabel: 'Título (opcional)',
    fileTitlePlaceholder: 'Por defecto usa el nombre del archivo',

    addingCta: 'Añadiendo…',
    addSourceCta: 'Añadir fuente',

    emptyTitle: 'Apúntalo a tu sitio',
    emptyDescription: 'Añade una URL, sitemap, texto, o archivo arriba y empezaremos a leerlo.',

    reviewPagesLink: 'Revisar páginas',
    nearEmptyInfoAriaLabel: ({ count }: { count: number }) =>
      `${count} ${count === 1 ? 'página parecía' : 'páginas parecían'} vacía(s)`,

    resyncCta: 'Resincronizar',
    deleteCta: 'Eliminar',
    cancelCta: 'Cancelar',
    syncInProgressError: 'Sincronización en curso — vuelve a intentarlo más tarde',

    deleteConfirmTitle: '¿Eliminar esta fuente?',
    deleteConfirmDescriptionPrefix: 'Elimina ',
    deleteConfirmDescriptionSuffix: ' y todo lo que indexó. Esto no se puede deshacer.',

    // RenderedModeToggle.tsx
    renderedModeExplainerAriaLabel: '¿Qué es el modo con renderizado?',
    renderedModeExplainerBody1Bold: 'El modo con renderizado',
    renderedModeExplainerBody1Rest:
      ' ejecuta cada página en un navegador real antes de leerla, para capturar contenido que solo aparece después de que se ejecuta el JavaScript.',
    renderedModeExplainerBody2:
      'Déjalo apagado para sitios normales — es más rápido y económico. Actívalo cuando una página se vea vacía para el crawler (aplicaciones de una sola página, dashboards, cualquier cosa que cargue su texto después de abrir la página).',
    renderedModeExplainerBody3:
      'Compensación: el rastreo es más lento y usa más de tu presupuesto de páginas por ejecución.',

    renderedModeLabel: 'Modo con renderizado',
    modeRendered: 'Con renderizado',
    modeStandard: 'Estándar',
    switchModeTitle: ({ mode }: { mode: string }) => `¿Cambiar al modo ${mode}?`,
    switchModeDescription: ({ mode }: { mode: string }) =>
      `Esto vuelve a rastrear la fuente en modo ${mode}. Las páginas que cambian se reindexan y las que ya no existen se eliminan — el índice actual sigue disponible hasta que termine el nuevo rastreo.`,
    switchModeCta: ({ mode }: { mode: string }) => `Cambiar al modo ${mode}`,
  },
  sourceDetail: {
    // lib/crawl-run.ts — describeCrawlRun
    crawledPages: ({ n }: { n: number }) => `Se ${n === 1 ? 'rastreó' : 'rastrearon'} ${n} ${n === 1 ? 'página' : 'páginas'}.`,
    crawledPagesWithSkipped: ({ n, failedCount }: { n: number; failedCount: number }) =>
      `Se ${n === 1 ? 'rastreó' : 'rastrearon'} ${n} ${n === 1 ? 'página' : 'páginas'}. ${failedCount} ${failedCount === 1 ? 'página no cargó y se omitió' : 'páginas no cargaron y se omitieron'}.`,
    truncatedCapacity: ({ n }: { n: number }) =>
      `Se ${n === 1 ? 'rastreó' : 'rastrearon'} ${n} ${n === 1 ? 'página' : 'páginas'} antes de que se agotara nuestra propia capacidad de rastreo. Esto es temporal de nuestro lado — espera unos minutos y vuelve a ejecutar el rastreo.`,
    truncatedDeadline: ({ n }: { n: number }) =>
      `Se ${n === 1 ? 'rastreó' : 'rastrearon'} ${n} ${n === 1 ? 'página' : 'páginas'} antes de que este rastreo alcanzara nuestro propio límite de tiempo.`,
    truncatedAttempts: ({ n }: { n: number }) =>
      `Se ${n === 1 ? 'rastreó' : 'rastrearon'} ${n} ${n === 1 ? 'página' : 'páginas'} antes de que el rastreo se quedara sin intentos en páginas que no cargaban.`,
    truncatedPageCap: ({ n }: { n: number }) =>
      `Se ${n === 1 ? 'rastreó' : 'rastrearon'} ${n} ${n === 1 ? 'página' : 'páginas'} — el máximo que permite el límite de páginas de tu plan ahora mismo.`,
    remainingAtLeast: ({ n }: { n: number }) =>
      ` Al menos ${n} ${n === 1 ? 'página no se indexó' : 'páginas no se indexaron'}.`,
    remainingNone: ' Es posible que algunas páginas no se hayan indexado.',
    crawlFailedGeneric: 'Este rastreo no indexó ninguna página.',

    // SourceDetail.tsx — typeLabel
    typeUrl: 'URL',
    typeSitemap: 'Sitemap',
    typeText: 'Texto',
    typeFile: 'Archivo',

    languagePrefix: 'Idioma: ',

    // Facts card
    factType: 'Tipo',
    factRenderedMode: 'Modo con renderizado',
    factLastSynced: 'Última sincronización',

    resyncCta: 'Resincronizar',
    deleteCta: 'Eliminar',

    latestCrawlHeading: 'Último rastreo',
    neverCrawledYet: 'Esta fuente todavía no se ha rastreado.',
    crawlRanInMode: ({ rendered }: { rendered: boolean }) =>
      `Este rastreo se ejecutó en modo ${rendered ? 'con renderizado' : 'estándar'}.`,

    // CrawlDiagnostics
    failedUrlsHeading: 'URLs fallidas',
    failedUrlsCountCaveat: ({ total, shown }: { total: number; shown: number }) =>
      ` — ${total} en total, mostrando las primeras ${shown}`,
    offSiteRedirectsHeading: 'Redirecciones fuera del sitio',
    offSiteRedirectsMoreCaveat: ' — puede haber más de las que muestra esta lista',
    nearEmptyCameBack: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'página volvió' : 'páginas volvieron'} con casi nada de contenido.`,

    pagesIndexedHeading: 'Páginas indexadas',
    nearEmptyOnlyToggle: 'Solo casi vacías',

    // DocumentsTable
    noPagesIndexedYet: 'Todavía no hay páginas indexadas.',
    colUrl: 'URL',
    colCharacters: 'Caracteres',
    colStatus: 'Estado',
    nearEmptyBadge: 'casi vacía',

    // indexStateLabel
    indexStateIndexed: 'Indexada',
    indexStateIndexing: 'Indexando…',
    indexStateFailed: 'Fallida',

    // DocumentContentViewer
    fileNoMarkdownExplainer:
      'Almacenado como el archivo subido en sí — conservamos el original, no una copia de texto separada, así que aquí no hay nada que previsualizar. El archivo está indexado y el bot responde con él.',
    nothingStoredForDocument: 'No hay nada almacenado para este documento.',

    storedContentHeading: 'Contenido almacenado',
    nothingStoredYet: 'Todavía no hay nada almacenado.',

    deleteDialogTitle: '¿Eliminar esta fuente?',
    deleteDialogDescriptionPrefix: 'Elimina ',
    deleteDialogDescriptionSuffix: ' y todo lo que indexó. Esto no se puede deshacer.',
    cancelCta: 'Cancelar',
  },
  conversations: {
    today: 'Hoy',
    yesterday: 'Ayer',
    yourTesting: 'Tus pruebas',
    visitor: 'Visitante',
    playgroundTag: 'Playground',
    noMessageFallback: '(sin mensaje)',
    leadCapturedTag: 'Lead capturado',
    hasUnansweredTag: 'Tiene sin responder',
    unansweredChipLabel: 'Sin responder',
    loadingMore: 'Cargando más…',
    loadMore: 'Cargar más',
    selectConversationPrompt: 'Selecciona una conversación para ver su transcripción.',
    backToConversations: '← Volver a conversaciones',
    transcriptStartedSummary: ({ time, n }: { time: string; n: number }) =>
      `Iniciada ${time} · ${n} mensajes`,
    deleteConversationAriaLabel: 'Eliminar esta conversación',
    leadSubmittedTag: '✓ Lead enviado',
    cancelCta: 'Cancelar',
    deletingCta: 'Eliminando…',

    pageTitle: 'Conversaciones',
    headerSubtitle: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'conversación' : 'conversaciones'} · más recientes primero`,
    emptyTitle: 'Esperando a tu primer visitante',
    emptyDescription: 'Las conversaciones aparecerán aquí en cuanto alguien chatee con tu bot.',

    deleteAllCta: 'Eliminar todas las conversaciones',
    deleteDialogTitle: '¿Eliminar esta conversación?',
    deleteDialogDescription:
      'Elimina permanentemente esta transcripción y sus mensajes. Un lead capturado en ella permanece en la pestaña Leads. Esto no se puede deshacer.',
    deleteCta: 'Eliminar conversación',

    deleteAllDialogTitle: '¿Eliminar todas las conversaciones?',
    deleteAllDescriptionPrefix:
      'Elimina permanentemente cada conversación y mensaje de este bot. Los leads capturados permanecen en la pestaña Leads. Esto no se puede deshacer. Escribe ',
    deleteAllDescriptionSuffix: ' para confirmar.',
    confirmBotNameAriaLabel: 'Confirmar nombre del bot',
  },
  leads: {
    filterNew: 'Nuevos',
    filterContacted: 'Contactados',
    filterArchived: 'Archivados',
    filterAll: 'Todos',
    actionMarkNew: 'Marcar como nuevo',
    actionMarkContacted: 'Marcar como contactado',
    actionArchive: 'Archivar',
    statusNew: 'nuevo',
    statusContacted: 'contactado',
    statusArchived: 'archivado',

    filterByStatusAriaLabel: 'Filtrar por estado',
    pageTitle: 'Leads',
    exportingCta: 'Exportando…',
    exportCsvCta: 'Exportar CSV',
    exportCsvError: 'No se pudieron exportar los leads — vuelve a intentarlo.',

    headerSubtitleAll: ({ n }: { n: number }) =>
      `${n} ${n === 1 ? 'lead capturado' : 'leads capturados'} · más recientes primero`,
    headerSubtitleFiltered: ({ n, filterLabel }: { n: number; filterLabel: string }) =>
      `${n} ${n === 1 ? 'lead' : 'leads'} ${filterLabel} · más recientes primero`,

    deleteAllCta: 'Eliminar todos los leads',
    bulkActionsAriaLabel: 'Acciones masivas',
    selectedCount: ({ n }: { n: number }) => `${n} seleccionados`,
    bulkCapMessage: ({ max }: { max: number }) => `Selecciona hasta ${max} leads a la vez`,
    bulkUpdatedMessage: ({ n }: { n: number }) => `${n} actualizados`,
    bulkUpdateError: 'No se pudieron actualizar esos leads. Vuelve a intentarlo.',

    emptyTitle: 'Esperando a tu primer lead',
    emptyDescription: 'Los leads capturados por el widget de chat de tu bot aparecerán aquí.',
    noFilterResultsTitle: ({ filterLabel }: { filterLabel: string }) => `Sin leads ${filterLabel}`,
    noFilterResultsDescription: 'Nada en este filtro por ahora.',

    selectAllAriaLabel: 'Seleccionar todos los leads',
    colName: 'Nombre',
    colContact: 'Contacto',
    colIntent: 'Intención',
    colStatus: 'Estado',
    colCaptured: 'Capturado',
    colActions: 'Acciones',

    noEmailFallback: '(sin email)',
    selectLeadAriaLabel: ({ contact }: { contact: string }) => `Seleccionar lead ${contact}`,
    deleteLeadAriaLabel: ({ contact }: { contact: string }) => `Eliminar lead ${contact}`,
    playgroundTag: 'Playground',
    viewChatCta: 'Ver chat',
    loadingMoreCta: 'Cargando más…',
    loadMoreCta: 'Cargar más',

    cancelCta: 'Cancelar',
    deletingCta: 'Eliminando…',

    deleteDialogTitle: '¿Eliminar este lead?',
    deleteDescriptionPrefix: 'Elimina permanentemente ',
    deleteDescriptionSuffix: '. Esto no se puede deshacer.',
    noContactFallback: 'este lead',
    deleteCta: 'Eliminar lead',

    deleteAllDialogTitle: '¿Eliminar todos los leads?',
    deleteAllDescriptionPrefix:
      'Elimina permanentemente cada lead capturado para este bot. Esto no se puede deshacer. Escribe ',
    deleteAllDescriptionSuffix: ' para confirmar.',
    confirmBotNameAriaLabel: 'Confirmar nombre del bot',
  },
  unanswered: {
    tabOpen: 'Abiertas',
    tabDismissed: 'Descartadas',
    tabAnswered: 'Respondidas',
    filterByStatusAriaLabel: 'Filtrar por estado',

    pageTitle: 'Sin responder',
    headerSubtitle: ({ n, verb }: { n: number; verb: string }) =>
      `${n} ${n === 1 ? 'pregunta' : 'preguntas'} ${verb}`,
    subtitleVerbOpen: 'esperando una respuesta',
    subtitleVerbDismissed: 'descartada(s)',
    subtitleVerbAnswered: 'respondida(s)',

    openTabIntro:
      'Preguntas que tu bot no pudo responder, agrupadas. Escribe una respuesta una vez — se convierte en una fuente de P&R y el bot la sabrá para siempre.',

    emptyTitleOpen: 'Nada abierto — tu bot respondió todo lo que le preguntaron.',
    emptyTitleDismissed: 'Nada descartado.',
    emptyTitleAnswered: 'Todavía no hay preguntas respondidas.',

    askedOnce: 'Preguntado una vez',
    askedTimes: ({ n }: { n: number }) => `Preguntado ${n} veces`,

    viewSourceCta: 'Ver fuente',
    sourceRemoved: 'fuente eliminada',
    viewChatCta: 'Ver chat',
    chatDeleted: 'chat eliminado',

    dismissCta: 'Descartar',
    reopenCta: 'Reabrir',
    writeAnswerCta: 'Escribir respuesta',
    cancelCta: 'Cancelar',
    answerPlaceholder: 'Escribe la respuesta que le darías a este visitante. El bot la usará de ahora en adelante.',
    addingCta: 'Añadiendo…',
    addAsQaSourceCta: 'Añadir como fuente de P&R',
    answerAddedNotice: 'Añadida como fuente de P&R — el bot la usará desde la próxima conversación.',
    saveAnswerError: 'No se pudo guardar esa respuesta — vuelve a intentarlo.',
  },
  appearance: {
    pageTitle: 'Apariencia',
    pageSubtitle: 'Cómo se ve el widget y cómo saluda a los visitantes en tu sitio',

    colorLabel: 'Color',
    colorHexAriaLabel: 'Valor hexadecimal del color',
    botNameLabel: 'Nombre del bot',
    botNamePlaceholder: 'Soporte de Acme',
    welcomeMessageLabel: 'Mensaje de bienvenida',
    welcomePlaceholder: '¡Hola! ¿Cómo puedo ayudarte hoy?',

    quickQuestionsLabel: 'Preguntas rápidas',
    addQuestionCta: 'Añadir pregunta',
    quickQuestionsHint: 'Opcional — se muestran como chips que los visitantes pueden tocar antes de escribir nada.',
    quickQuestionAriaLabel: ({ n }: { n: number }) => `Pregunta rápida ${n}`,
    quickQuestionPlaceholder: '¿Cuál es su horario de atención?',
    removeCta: 'Quitar',

    positionLabel: 'Posición del lanzador',
    positionBottomRight: 'Abajo a la derecha',
    positionBottomLeft: 'Abajo a la izquierda',

    languageLabel: 'Idioma',
    autoLanguageLabel: 'Según el navegador del visitante',

    badgeLabel: 'Insignia "Powered by Embent"',
    badgeHint: 'Muestra un pequeño enlace "Powered by Embent" en el pie del widget.',
    badgeSwitchAriaLabel: 'Mostrar la insignia "Powered by Embent"',
    badgeLoadError: 'No se pudo cargar — actualiza la página',
    freeBadgeNote: ({ plans }: { plans: string }) => `Actualizar a ${plans} elimina este requisito.`,

    saveCta: 'Guardar',
    savingCta: 'Guardando…',
    savedLabel: 'Guardado',

    livePreviewHeading: 'Vista previa en vivo',
    previewCaption:
      'Aquí solo se renderiza el botón lanzador y el panel — el resto de la página está en blanco a propósito.',
    devHintPrefix: 'Si no aparece nada, el paquete del widget no está compilado localmente: ejecuta',
    devHintIn: 'en',
    devHintAndCopy: 'y copia',
    devHintTo: 'a',

    // WidgetPreview.tsx
    widgetPreviewTitle: 'Vista previa del widget',
  },
  botSettings: {
    pageTitle: 'Configuración',
    pageSubtitle: 'Dominios permitidos, captura de leads, y cómo se presenta el bot',

    leadCaptureOffLabel: 'Apagado',
    leadCaptureOffBlurb: 'El bot nunca pide datos de contacto a los visitantes.',
    leadCaptureOnIntentLabel: 'Según intención',
    leadCaptureOnIntentBlurb:
      'El bot pide un email o un número de teléfono solo cuando no puede responder o el visitante quiere seguimiento.',
    leadCaptureAlwaysLabel: 'Siempre',
    leadCaptureAlwaysBlurb:
      'El bot pide datos de contacto una vez, justo después del primer intercambio en una conversación.',
    leadCapturePreChatLabel: 'Formulario previo al chat',
    leadCapturePreChatBlurb:
      'Los visitantes completan un breve formulario de contacto (nombre, email, teléfono opcional) antes de que empiece el chat. El bot nunca vuelve a preguntar dentro del chat.',

    leadLevelSubtleLabel: 'Sutil',
    leadLevelSubtleBlurb: 'Solo ofrece seguimiento cuando el visitante pide que lo contacten o quiere una cotización.',
    leadLevelBalancedLabel: 'Equilibrado (recomendado)',
    leadLevelBalancedBlurb:
      'Ofrece una vez cuando el visitante muestra señales reales de compra — pregunta cómo empezar, quiere una demo, o menciona su propio sitio.',
    leadLevelProactiveLabel: 'Proactivo',
    leadLevelProactiveBlurb:
      'Ofrece siempre que el visitante parezca estar evaluando, y vuelve a preguntar más tarde si la primera oferta quedó sin respuesta.',

    domainRequiredError: 'Requerido — elimina la fila en lugar de dejarla en blanco',
    domainBareHostnameError: 'Solo el nombre de host, por ejemplo "ejemplo.com" (sin esquema ni ruta)',
    domainTooLongError: 'Demasiado largo',

    aboutLabel: 'Sobre este bot',
    aboutHint:
      'Una breve presentación que el bot siempre conoce — quién es, a quién sirve — para que preguntas como ' +
      '"¿quién eres?" se respondan al instante sin necesidad de buscar.',
    aboutPlaceholder:
      'Somos el asistente de soporte de Acme, aquí para ayudar a los clientes a configurar y resolver problemas con Acme Widgets.',

    allowedDomainsLabel: 'Dominios permitidos',
    addDomainCta: 'Añadir dominio',
    allowedDomainsHint:
      'Sitios autorizados para insertar el widget de este bot. Déjalo vacío para permitir cualquier sitio — se recomienda ' +
      'restringirlo a tu(s) propio(s) dominio(s) antes de publicarlo.',
    domainAriaLabel: ({ n }: { n: number }) => `Dominio permitido ${n}`,
    domainPlaceholder: 'example.com',
    removeCta: 'Quitar',

    leadCaptureLabel: 'Captura de leads',
    leadLevelLabel: '¿Qué tan proactivo?',

    saveCta: 'Guardar',
    savingCta: 'Guardando…',
    savedLabel: 'Guardado',

    embedSnippetHeading: 'Código de inserción',
    embedPastePrefix: 'Pega esto antes de la etiqueta de cierre',
    embedPasteSuffix: 'en tu sitio.',

    embedCopyCta: 'Copiar',
    embedCopiedCard: '¡Copiado!',
    embedCopiedCheck: '✓ Copiado',

    dangerZoneHeading: 'Zona de peligro',
    dangerZoneDescription:
      'Elimina el bot, sus fuentes, conversaciones, leads, y su índice de búsqueda. Esto no se puede deshacer.',
    deleteBotCta: 'Eliminar este bot',
    deleteBotDialogTitle: ({ name }: { name: string }) => `¿Eliminar "${name}"?`,
    dangerZoneDialogDescriptionPrefix:
      'Elimina el bot, sus fuentes, conversaciones, leads, y su índice de búsqueda. Esto no se puede deshacer. Escribe ',
    dangerZoneDialogDescriptionSuffix: ' para confirmar.',
    confirmBotNameAriaLabel: 'Confirmar nombre del bot',
    cancelCta: 'Cancelar',
    deletingCta: 'Eliminando…',
  },
  notifications: {
    heading: 'Notificaciones',
    subheading: 'Los emails llegan a la dirección de cuenta de cada persona. La actividad en Playground nunca dispara emails.',
    noMembers: 'No hay miembros.',
    saveError: 'No se pudo guardar ese ajuste. Vuelve a intentarlo.',

    roleOwner: 'Propietario',
    roleAdmin: 'Admin',
    roleMember: 'Miembro',
    cadenceOff: 'Apagado',
    cadenceDaily: 'Diario',
    cadenceWeekly: 'Semanal',

    colMember: 'Miembro',
    colNewLead: 'Nuevo lead',
    colUnansweredDigest: 'Resumen de sin responder',
    colCrawlFailed: 'Rastreo fallido',
    colWeeklySummary: 'Resumen semanal',

    newLeadAriaLabel: ({ email }: { email: string }) => `Emails de nuevo lead para ${email}`,
    unansweredDigestAriaLabel: ({ email }: { email: string }) => `Emails de resumen de sin responder para ${email}`,
    crawlFailedAriaLabel: ({ email }: { email: string }) => `Emails de rastreo fallido para ${email}`,
    weeklySummaryAriaLabel: ({ email }: { email: string }) => `Emails de resumen semanal para ${email}`,
  },
  playground: {
    pageTitle: 'Playground',
    pageSubtitle: 'Chatea con tu bot como lo haría un visitante — nada de esto llega a tu sitio.',
    retrievalDebugLabel: 'Debug de búsqueda',
    hideDebugCta: 'Ocultar debug',
    newSessionCta: 'Nueva sesión',
    emptyStateText: 'Envía un mensaje para probar cómo responde este bot, basado en sus fuentes.',
    inputPlaceholder: 'Hazle una pregunta a este bot…',
    stopCta: 'Detener',
    sendCta: 'Enviar',
    debugEmptyText: 'Haz una pregunta para ver qué recupera',
    genericError: 'Algo salió mal.',
  },
  billing: {
    pageTitle: 'Facturación',
    openingCta: 'Abriendo…',
    manageBillingCta: 'Administrar facturación',

    memberGateTitle: 'La facturación la administra el propietario del workspace o un admin.',
    memberGateDescription: 'Pídele a un propietario o admin que haga cambios en el plan de este workspace.',

    nowOnPlan: ({ name }: { name: string }) => `Ahora estás en el plan ${name}.`,
    finishingUp: 'Estamos terminando esto — actualiza en un momento si tu plan no se actualiza.',
    confirmingWithPolar: 'Cambio recibido — confirmando con Polar…',

    internalWorkspaceLabel: 'Workspace interno',
    monthlyPriceLabel: ({ price }: { price: number }) => `$${price} / mes`,
    internalWorkspaceInline: 'Sin límites, la facturación no aplica.',
    internalWorkspaceHeading: 'Workspace interno — sin límites, la facturación no aplica.',
    internalWorkspaceBody:
      'Se ignora cada límite de plan y no hay nada que actualizar ni pagar aquí. El uso se sigue ' +
      'registrando arriba por visibilidad, pero nunca se envía a Polar — este workspace no tiene ' +
      'identidad de facturación.',

    unlimitedWord: 'ilimitado',
    unlimitedParens: '(ilimitado)',
    ofCap: ({ cap }: { cap: string }) => `/ ${cap}`,
    internalRenewalFallback: 'Workspace interno · sin límites · la facturación no aplica',
    capsSuffix: ({
      bots,
      botsCount,
      messages,
      pages,
    }: {
      bots: string;
      botsCount: number | null;
      messages: string;
      pages: string;
    }) => `${bots} ${botsCount === 1 ? 'bot' : 'bots'} · ${messages} mensajes · ${pages} páginas`,
    freeForever: ({ capsSuffix }: { capsSuffix: string }) => `Gratis para siempre · ${capsSuffix}`,
    renewsOn: ({ date, capsSuffix }: { date: string; capsSuffix: string }) =>
      `Se renueva el ${date} · ${capsSuffix}`,

    cancelsAtPeriodEnd: 'Se cancela al final del período',
    planChangesPausedNotice: 'Los cambios de plan están pausados hasta que termine el período actual.',
    planChangesPausedReason:
      'Esta suscripción está configurada para cancelarse al final del período — reanúdala primero para cambiar de plan.',

    redirectingCta: 'Redirigiendo…',
    switchingCta: 'Cambiando…',
    upgradeToCta: ({ name }: { name: string }) => `Actualizar a ${name}`,

    messagesThisMonthLabel: 'Mensajes este mes',
    pagesIndexedLabel: 'Páginas indexadas',
    messageNoun: 'mensaje',
    pageNoun: 'página',
    messagesUnit: ({ n }: { n: string }) => `${n} mensajes`,
    usageLimitWarning: ({ pct, noun }: { pct: number; noun: string }) =>
      `${pct}% de tu límite de ${noun}`,
    upgradeRaisesTo: ({ name, unit }: { name: string; unit: string }) =>
      ` — ${name} lo eleva a ${unit}.`,
    limitReached: ' alcanzado.',

    cancelSubscriptionCta: 'Cancelar suscripción',
    cancelingCta: 'Cancelando…',
    cancelDialogTitle: '¿Cancelar suscripción?',
    yourCurrentPlanFallback: 'tu plan actual',
    cancelDialogDescription: ({
      planName,
      renewsDate,
    }: {
      planName: string;
      renewsDate: string | null;
    }) =>
      `Tus bots siguen funcionando en ${planName} hasta el final de este período de facturación${
        renewsDate ? ` (${renewsDate})` : ''
      } — luego el workspace baja a Free. Esto nunca ocurre de inmediato.`,
    keepSubscriptionCta: 'Mantener suscripción',

    changePlanFallbackTitle: '¿Cambiar de plan?',
    keepCurrentPlanCta: 'Mantener plan actual',
    confirmFallbackCta: 'Confirmar',

    checkoutErrorFallback: 'Algo salió mal al iniciar el pago.',
    portalErrorFallback: 'Algo salió mal al abrir la administración de facturación.',
    cancelErrorFallback: 'Algo salió mal al cancelar tu suscripción.',

    // PlanCard
    currentPlanBadge: 'Plan actual',
    botsUnit: ({ n }: { n: number }) => `${n} ${n === 1 ? 'bot' : 'bots'}`,
    seatsUnit: ({ n }: { n: number }) => `${n} ${n === 1 ? 'usuario' : 'usuarios'}`,
    messagesPerMonth: ({ n }: { n: string }) => `${n} mensajes / mes`,
    pagesUnit: ({ n }: { n: string }) => `${n} páginas`,
    badgeRequiredText: 'Insignia "Powered by Embent"',
    badgeOptionalText: 'Insignia "Powered by Embent" opcional',
    upgradeCta: 'Actualizar',
    switchToCta: ({ name }: { name: string }) => `Cambiar a ${name}`,

    flatPricingNote:
      'Precio fijo, sin cálculos por asiento, sin cargos sorpresa — te avisamos al 80% y solo pausamos ' +
      'respuestas nuevas al pasar el 110%, y nunca cortamos una conversación a mitad de respuesta.',

    // lib/billing-copy.ts — planChangeConfirmCopy
    planChangeConfirm: {
      verbUpgrade: 'Actualizar',
      verbSwitch: 'Cambiar',
      title: ({ verb, name }: { verb: string; name: string }) => `¿${verb} a ${name}?`,
      confirmNow: ({ verb }: { verb: string }) => `${verb} ahora`,
      continueToPolarCta: 'Continuar a Polar',
      checkoutDescription: ({ name, price }: { name: string; price: number }) =>
        `Se te llevará a Polar para iniciar una suscripción a ${name} ($${price}/mes) — Polar te ` +
        'cobra ahí para activarla.',
      switchDescription: ({ name, price }: { name: string; price: number }) =>
        `Esto cambia tu plan de inmediato — tus bots empiezan a usar los límites de ${name} ` +
        `($${price}/mes) al instante. Polar no te cobrará hoy: aplica la diferencia prorrateada ` +
        'a tu próxima factura.',
    },
  },
  workspace: {
    pageTitle: 'Configuración del workspace',
    loadErrorMessage: 'No se pudo cargar este workspace. Recarga la página para volver a intentarlo.',
    notAllowedTitle: 'Solo los propietarios y admins pueden cambiar la configuración del workspace.',
    notAllowedDescription: 'Pídele a un propietario o admin de este workspace que haga los cambios.',

    nameLabel: 'Nombre del workspace',
    nameHint: ({ min, max }: { min: number; max: number }) =>
      `${min}–${max} caracteres. Se muestra a todos en este workspace y en los emails de invitación.`,

    uploadLogoCta: 'Subir logo',
    removeLogoCta: 'Quitar',
    logoHint: 'Un formato cuadrado funciona mejor. PNG, JPEG, WebP o GIF, hasta 2 MB.',
    logoNotConfiguredError: 'La subida de logos no está configurada en este entorno.',
    uploadLogoErrorFallback: 'No se pudo subir el logo. Vuelve a intentarlo.',
    removeLogoErrorFallback: 'No se pudo quitar el logo. Vuelve a intentarlo.',

    saveNameErrorFallback: 'No se pudo guardar el nombre. Vuelve a intentarlo.',
    saveCta: 'Guardar',
    savedLabel: 'Guardado',

    // LogoCropDialog.tsx
    cropDialogTitle: 'Recorta tu logo',
    cropDialogDescription: 'Arrastra para reposicionar, haz zoom para ajustar. Se guarda como un cuadrado de 256×256.',
    zoomLabel: 'Zoom',
    cropErrorFallback: 'No se pudo recortar esta imagen. Prueba con otro archivo.',
    cancelCta: 'Cancelar',
    useThisLogoCta: 'Usar este logo',
  },
  members: {
    pageTitle: 'Miembros',
    membersOnlyTitle: 'Área exclusiva para miembros',
    membersOnlyDescription: 'Pídele a tu propietario o admin del workspace que administre los miembros.',
    unlimitedCapWord: 'Ilimitado',
    seatsSubtitle: ({
      used,
      cap,
      pending,
    }: {
      used: number;
      cap: string | number;
      pending: number;
    }) => `${used} de ${cap} usuarios usados${pending > 0 ? ` (${pending} invitados)` : ''}`,
    inviteMemberCta: 'Invitar miembro',

    roleOwner: 'Propietario',
    roleAdmin: 'Admin',
    roleMember: 'Miembro',
    roleOptionMember: 'Miembro',
    roleOptionAdmin: 'Admin',

    colName: 'Nombre',
    colEmail: 'Email',
    colRole: 'Rol',
    leaveWorkspaceCta: 'Salir del workspace',
    removeCta: 'Quitar',

    pendingInvitesHeading: 'Invitaciones pendientes',
    noPendingInvites: 'No hay invitaciones pendientes.',
    inviteColEmail: 'Email',
    inviteColRole: 'Rol',
    inviteColExpires: 'Caduca',
    revokingCta: 'Revocando…',
    revokeCta: 'Revocar',

    cancelCta: 'Cancelar',
    removingCta: 'Quitando…',
    leaveDialogTitle: '¿Salir de este workspace?',
    removeDialogTitle: ({ name }: { name: string }) => `¿Quitar a ${name}?`,
    leaveDialogDescription: 'Perderás el acceso de inmediato — un propietario o admin puede volver a invitarte después.',
    removeDialogDescription: ({ name }: { name: string }) => `${name} pierde el acceso a este workspace de inmediato.`,

    emailLabel: 'Email',
    emailPlaceholder: 'teammate@example.com',
    roleLabel: 'Rol',
    inviteErrorFallback: 'Algo salió mal al enviar esta invitación.',
    sendingInviteCta: 'Enviando…',
    sendInviteCta: 'Enviar invitación',

    roleChangeErrorFallback: 'No se pudo cambiar este rol.',
    removeErrorFallback: 'No se pudo quitar a este miembro.',
    revokeErrorFallback: 'No se pudo revocar esta invitación.',
  },
  account: {
    pageTitle: 'Cuenta',
    loadSignInMethodsError: 'No se pudieron cargar los métodos de inicio de sesión',
    emailPasswordLabel: 'Email y contraseña',
    googleLabel: 'Google',

    savingCta: 'Guardando…',

    profileHeading: 'Perfil',
    displayNameLabel: 'Nombre para mostrar',
    namePlaceholder: 'Tu nombre',
    emailLabel: 'Email',
    saveNameErrorFallback: 'Algo salió mal al guardar tu nombre.',
    saveCta: 'Guardar',
    savedLabel: 'Guardado',

    passwordHeading: 'Contraseña',
    passwordMismatchError: 'La nueva contraseña y su confirmación no coinciden.',
    currentPasswordLabel: 'Contraseña actual',
    newPasswordLabel: 'Nueva contraseña',
    confirmNewPasswordLabel: 'Confirmar nueva contraseña',
    changePasswordErrorFallback: 'Algo salió mal al cambiar tu contraseña.',
    changePasswordCta: 'Cambiar contraseña',
    passwordChangedLabel: 'Contraseña cambiada',
  },
  adminImpersonate: {
    impersonateFailedFallback: 'No se pudo suplantar a este usuario.',
    lookupFailedFallback: 'No se pudo buscar a este usuario.',
    userNotFound: 'Cuenta de usuario no encontrada.',
    noOwnerToView: 'Este workspace no tiene propietario para ver como él.',
  },
  admin: {
    pagination: {
      pageOfTotal: ({
        page,
        totalPages,
        totalCount,
      }: {
        page: number;
        totalPages: number;
        totalCount: string;
      }) => `Página ${page} de ${totalPages} · ${totalCount} en total`,
      prevCta: 'Ant.',
      nextCta: 'Sig.',
    },
    workspaces: {
      // CrawlSignalPanel
      crawlSignalRenderedLabel: 'Depende del rastreo con modo renderizado',
      crawlSignalSourcesAcrossBots: ({
        sourceCount,
        botCountLabel,
        botCount,
      }: {
        sourceCount: number;
        botCountLabel: string;
        botCount: number;
      }) =>
        `${sourceCount === 1 ? 'fuente' : 'fuentes'} en ${botCountLabel} ${botCount === 1 ? 'bot' : 'bots'}`,
      crawlSignalRenderedFootnote:
        'Cuánto de la flota depende de Cloudflare Browser Rendering — capacidad que tenemos en el plan Workers Free hoy, no en Paid.',
      crawlSignalCapacityLabel: 'Alcanzó nuestro propio límite de capacidad de renderizado (7d)',
      crawlSignalSourceCountLabel: ({ n }: { n: number }) => `${n === 1 ? 'fuente' : 'fuentes'}`,
      crawlSignalCapacityFootnote:
        'Fuentes distintas, solo nuestra propia capacidad — nunca el límite de plan de un cliente. Un proceso eliminado o un job obsoleto recogido no deja registro — esto es un piso. Si sube de forma constante, es hora de Workers Paid ($5/mes).',

      // AdminWorkspaces.tsx
      emptyText: 'Todavía no hay workspaces.',
      internalBadge: 'INTERNO',
      cancelsAtPeriodEndHint: 'se cancela al final del período',
      colName: 'Nombre',
      colOwner: 'Propietario',
      colPlan: 'Plan',
      colBots: 'Bots',
      colMembers: 'Miembros',
      colMsgsPerMo: 'Mens. / mes',
      colPages: 'Páginas',
      colCreated: 'Creado',
      colActions: 'Acciones',
      noOwnerTitle: 'Este workspace no tiene propietario',
      viewAsCta: 'Ver como',
      switchingCta: 'Cambiando…',
      viewAsErrorFallback: 'Falló',

      // AdminWorkspaceDetail.tsx
      backToWorkspaces: '← Volver a Workspaces',
      ownerLabel: 'Propietario: ',
      bannedBadge: 'baneado',
      createdLabel: ({ date }: { date: string }) => `Creado ${date}`,
      usageHeading: 'Uso',
      messagesThisMonthLabel: 'Mensajes este mes',
      pagesIndexedLabel: 'Páginas indexadas',
      botsLabel: 'Bots',
      internalUsageNote: 'Workspace interno — no aplica ningún límite; el uso se sigue registrando arriba.',
      botsHeading: 'Bots',
      noBotsYet: 'Todavía no hay bots.',
      colBotName: 'Nombre',
      colPublicId: 'ID público',
      colStatus: 'Estado',
      colBotCreated: 'Creado',
      membersHeading: 'Miembros',
      noMembersYet: 'No hay miembros.',
      colEmail: 'Email',
      colMemberName: 'Nombre',
      colRole: 'Rol',
      colJoined: 'Se unió',
      memberRoleLabel: {
        owner: 'Propietario',
        admin: 'Admin',
        member: 'Miembro',
      },
      subscriptionHeading: 'Suscripción',
      statusLabel: 'Estado: ',
      periodEndLabel: 'Fin del período: ',
      cancelsAtPeriodEndLabel: 'Se cancela al final del período: ',
      yesLabel: 'Sí',
      noLabel: 'No',
      polarSubscriptionIdLabel: 'ID de suscripción en Polar: ',
      manageInPolarLink: 'Administrar en Polar ↗',
      noSubscriptionText: 'Sin suscripción — este workspace está en el plan gratuito.',
      dangerZoneHeading: 'Zona de peligro',
      internalToggleLabel: 'Workspace interno',
      internalToggleHint: 'Sin límites, sin facturación — el uso se sigue registrando pero nunca se envía a Polar.',
      banOwnerLabel: 'Banear propietario',
      banOwnerHint: 'Los usuarios baneados no pueden iniciar sesión. El widget/API pública no se ven afectados.',
      unbanCta: 'Quitar baneo',
      unbanningCta: 'Quitando baneo…',
      banCta: 'Banear',
      demoteBeforeBanTitle: 'Degrada a este admin a usuario antes de banearlo.',
      roleLabel: 'Rol',
      roleHint: 'Los admins pueden acceder a cada pantalla interna de administración.',
      cannotChangeOwnRoleTitle: 'No puedes cambiar tu propio rol.',
      cancelCta: 'Cancelar',
      savingCta: 'Guardando…',
      confirmCta: 'Confirmar',
      markInternalDialogTitle: '¿Marcar este workspace como interno?',
      removeInternalDialogTitle: '¿Quitar el estado interno?',
      markInternalDialogDescription:
        'Este workspace ignorará cada límite de plan (bots, mensajes, páginas) y nunca se le facturará. El uso se sigue registrando por visibilidad, pero el envío nocturno de uso a Polar lo omite — no hay identidad de facturación a la que enviarlo.',
      removeInternalDialogDescription:
        'Este workspace vuelve a la aplicación normal de plan y facturación (si tiene una suscripción activa).',
      banDialogTitle: ({ email }: { email: string | undefined }) => `¿Banear a ${email}?`,
      banDialogDescription: 'Esto lo desconecta de inmediato y evita cualquier futuro inicio de sesión hasta que se le quite el baneo.',
      banReasonLabel: 'Motivo (opcional)',
      banReasonPlaceholder: '¿Por qué se está baneando esta cuenta?',
      banningCta: 'Baneando…',
      banUserCta: 'Banear usuario',
      roleDialogTitle: ({ role }: { role: 'user' | 'admin' | null }) => `¿Cambiar el rol a ${role}?`,
      roleDialogDescriptionAdmin: ({ email }: { email: string | undefined }) =>
        `${email} ganará acceso a cada pantalla interna de administración.`,
      roleDialogDescriptionUser: ({ email }: { email: string | undefined }) =>
        `${email} perderá el acceso a las pantallas internas de administración.`,
      updateWorkspaceErrorFallback: 'No se pudo actualizar este workspace.',
      banUserErrorFallback: 'No se pudo banear a este usuario.',
      unbanUserErrorFallback: 'No se pudo quitar el baneo a este usuario.',
      changeRoleErrorFallback: 'No se pudo cambiar el rol de este usuario.',
    },
    bots: {
      // AdminBots.tsx
      colName: 'Nombre',
      colPublicId: 'ID público',
      colWorkspace: 'Workspace',
      colStatus: 'Estado',
      colSources: 'Fuentes',
      colLastCrawl: 'Último rastreo',
      colInstanceId: 'ID de instancia',
      noBotsYet: 'Todavía no hay bots.',

      // AdminBotDetail.tsx
      backToBots: '← Volver a Bots',
      workspaceLabel: 'Workspace: ',
      instanceLabel: ({ id }: { id: string }) => `Instancia: ${id}`,
      createdLabel: ({ date }: { date: string }) => `Creado ${date}`,
      openOwnerViewCta: 'Abrir vista del propietario',
      openingCta: 'Abriendo…',
      openOwnerViewErrorFallback: 'No se pudo abrir la vista del propietario.',
      sourcesHeading: 'Fuentes',
      noSourcesYet: 'Todavía no hay fuentes.',
      colUrl: 'URL',
      colType: 'Tipo',
      colSourceStatus: 'Estado',
      colPages: 'Páginas',
      colLastCrawled: 'Último rastreo',
      colError: 'Error',
      recentJobsHeading: 'Jobs recientes',
      noJobsLinked: 'Todavía no hay jobs vinculados a este bot.',
      colKind: 'Tipo',
      colJobStatus: 'Estado',
      colAttempts: 'Intentos',
      colUpdated: 'Actualizado',
      colPayload: 'Payload',
      recentJobsFootnote:
        'Los jobs recientes se emparejan de la mejor forma posible según el payload de cada job — un job que no lleve el id de este bot ni el de una de sus fuentes no aparecerá aquí, aunque haya afectado a este bot indirectamente.',
    },
    jobs: {
      tabLabel: {
        all: 'Todos',
        running: 'En ejecución',
        failed: 'Fallidos',
      },
      noFailedJobs: 'No hay jobs fallidos — la cola está saludable.',
      noJobsToShow: 'No hay jobs para mostrar.',
      colKind: 'Tipo',
      colStatus: 'Estado',
      colPayload: 'Payload',
      colAttempts: 'Intentos',
      colCreated: 'Creado',
      colUpdated: 'Actualizado',
      colActions: 'Acciones',
      retryCta: 'Reintentar',
      retryingCta: 'Reintentando…',
      cancelCta: 'Cancelar',
      cancellingCta: 'Cancelando…',
      cancelJobCta: 'Cancelar job',
      keepJobCta: 'Mantener job',
      cancelDialogTitle: '¿Cancelar este job?',
      retryJobErrorFallback: 'No se pudo reintentar este job.',
      cancelJobErrorFallback: 'No se pudo cancelar este job.',
      cancelDialogDescriptionListRow:
        'Esto elimina la fila del job pendiente — nunca se ejecutará. Solo se pueden cancelar así los jobs pendientes; un job en ejecución puede tener su handler ya trabajando sobre él.',

      // AdminJobDetail.tsx
      backToJobs: '← Volver a Jobs',
      jobIdLabel: 'ID del job',
      attemptsLabel: 'Intentos',
      createdLabel: 'Creado',
      updatedLabel: 'Actualizado',
      runAfterLabel: 'Ejecutar después de',
      errorHeading: 'Error',
      payloadHeading: 'Payload',
      cancelDialogDescriptionDetailPage: 'Esto elimina la fila del job pendiente — nunca se ejecutará.',
    },
    posts: {
      description:
        'Entradas del blog, borradores y publicadas. Editar aquí se publica en embent.com en menos de un minuto.',
      newPostCta: 'Nueva entrada',
      emptyText: 'Todavía no hay entradas — crea la primera.',
      colTitle: 'Título',
      colStatus: 'Estado',
      colAuthor: 'Autor',
      colPublished: 'Publicada',
      colUpdated: 'Actualizada',
      scheduledLabel: ({ date }: { date: string }) => `programada · ${date}`,
    },
    postEditor: {
      // Header
      backToPosts: '← Entradas',
      newPostTitle: 'Nueva entrada',
      editPostTitleFallback: 'Editar entrada',

      // Post fields
      titleLabel: 'Título',
      titlePlaceholder: 'Cómo responde Embent a las preguntas de soporte',
      slugLabel: 'Slug',
      slugPlaceholder: 'how-embent-answers-support-questions',
      slugUrlPrefix: 'embent.com/blog/',
      excerptLabel: 'Extracto',
      excerptPlaceholder:
        'Una o dos frases — se muestran en /blog y se usan como meta descripción.',
      tagsLabel: 'Etiquetas',
      tagsPlaceholder: 'rag, support, launch',
      tagsHint: 'Separadas por comas.',
      coverUrlLabel: 'URL de la imagen de portada',
      coverUrlPlaceholder: 'https://cdn.embent.com/blog/…',

      // Author picker
      authorLabel: 'Autor',
      editAuthorCta: 'Editar autor',
      newAuthorCta: '+ Nuevo autor',
      authorSelectPlaceholder: 'Elige un autor',
      noAuthorsYet: 'Todavía no hay autores — añade uno con "+ Nuevo autor" arriba.',

      // Publish / visibility
      visibilityScheduledText: ({ date }: { date: string }) => `Se publica el ${date}`,
      visibilityLiveText: ({ date }: { date: string }) => `Publicada el ${date}`,
      visibilityDraftText: 'Todavía no publicada',
      publishDateLabel: 'Fecha de publicación',
      publishDateHintPrefix: 'Las horas están en ',
      publishDateHintSuffix:
        ' — el reloj de este navegador. Déjalo vacío para publicarla en cuanto la publiques. Fija una hora futura y la entrada permanece oculta hasta entonces — desaparece del blog, de sus conteos de etiquetas, del sitemap y del feed RSS hasta que el reloj lo alcance. No hace falta que nada esté en ejecución para que aparezca.',

      // Body + preview
      bodyLabel: 'Cuerpo (Markdown)',
      livePreviewLabel: 'Vista previa en vivo',

      // Validation
      missingFieldLabel: {
        title: 'título',
        slug: 'slug',
        excerpt: 'extracto',
        body: 'cuerpo',
        author: 'autor',
      },
      beforeSavingAdd: ({ fields }: { fields: string }) => `Antes de guardar, añade: ${fields}.`,

      // Save / publish actions
      savingCta: 'Guardando…',
      saveChangesCta: 'Guardar cambios',
      saveDraftCta: 'Guardar borrador',
      unpublishingCta: 'Despublicando…',
      schedulingCta: 'Programando…',
      publishingCta: 'Publicando…',
      unpublishCta: 'Despublicar',
      scheduleCta: 'Programar',
      publishCta: 'Publicar',
      savedFlash: 'Guardado',

      // Preview pane
      renderingPreview: 'Renderizando…',
      previewFailedPrefix: 'La vista previa no se pudo cargar: ',

      // Markdown editor / image uploads
      uploadFailedFallback: 'La subida falló',
      bodyPlaceholder: 'Escribe en Markdown… pega o arrastra una imagen para subirla en línea.',
      uploadingText: 'Subiendo…',
      imageUploadFailedPrefix: 'La subida de la imagen falló: ',

      // Delete dialog
      deleteCta: 'Eliminar',
      deleteDialogTitle: ({ title }: { title: string }) => `¿Eliminar "${title}"?`,
      deleteDialogDescriptionPrefix: 'Elimina permanentemente esta entrada. Esto no se puede deshacer. Escribe',
      deleteDialogDescriptionSuffix: ' para confirmar.',
      confirmPostTitleAriaLabel: 'Confirmar título de la entrada',
      cancelCta: 'Cancelar',
      deletingCta: 'Eliminando…',
      deletePostCta: 'Eliminar entrada',

      // Author dialog
      editAuthorDialogTitle: ({ name }: { name: string }) => `Editar ${name}`,
      newAuthorDialogTitle: 'Nuevo autor',
      authorDialogDescription: 'Solo autorías reales — sin identidades inventadas.',
      authorNameLabel: 'Nombre',
      authorNamePlaceholder: 'Johnny Bui',
      authorRoleLabel: 'Rol',
      authorRolePlaceholder: 'Fundador',
      authorAvatarLabel: 'URL del avatar',
      authorAvatarPlaceholder: 'https://cdn.embent.com/…',
      authorBioLabel: 'Biografía',
      authorPersonaLabel: 'Persona (interno)',
      authorPersonaPlaceholder:
        'Escribe en frases cortas y directas. Escéptico del hype. Prefiere un ejemplo concreto a un adjetivo.',
      authorPersonaHint:
        'Una nota de referencia para redactar con la voz de esta autoría — nunca se publica, nunca se muestra a los lectores.',
      authorSavingCta: 'Guardando…',
      authorAddingCta: 'Añadiendo…',
      saveAuthorCta: 'Guardar autor',
      addAuthorCta: 'Añadir autor',

      // Writing guide panel (spec §G)
      writingGuideHeading: 'Guía de escritura',
      writingGuideSubtitle: 'Soporte de Markdown, imágenes, y una plantilla de prompt de IA',
      supportedMarkdownHeading: 'Markdown compatible',
      mdListHeadings: 'Encabezados, niveles 1 a 6',
      mdListLists: 'Listas con viñetas y numeradas',
      mdListLinksPrefix: 'Enlaces — ',
      mdListLinksSuffix: ', solo http(s) y mailto',
      mdListImages:
        'Imágenes en cualquier parte del cuerpo, incluido a mitad del artículo — pega o arrastra una (ver abajo)',
      mdListCode: 'Bloques de código en línea y con formato, con resaltado de sintaxis',
      mdListTables: 'Tablas, estilo GitHub, con alineación de columnas',
      mdListBlockquotes: 'Citas en bloque',
      strippedHeading: 'Qué se elimina',
      strippedPrefix: 'Todo lo demás. Las etiquetas HTML sin procesar, y el contenido de ',
      strippedSuffix:
        ' específicamente, nunca sobreviven — el cuerpo se sanitiza contra una lista fija permitida (encabezados, listas, enlaces, imágenes, código, tablas, citas en bloque, más el párrafo/énfasis con que se construyen) antes de renderizarse en cualquier lugar, incluida esta vista previa.',
      imagesHeading: 'Imágenes',
      imagesPrefix: 'Pega o arrastra una imagen dentro del cuerpo — se sube a cdn.embent.com e inserta',
      imagesSuffix:
        ' en el cursor, donde sea que esté, para que pueda quedar en medio de un párrafo. La imagen de portada de arriba es un campo separado — nunca proviene de algo soltado en el cuerpo.',
      copiedCta: '¡Copiado!',
      copyAiPromptCta: 'Copiar prompt de IA',
      copiedToClipboard: 'Copiado al portapapeles.',
      clipboardUnavailable:
        'El portapapeles no está disponible en este navegador — selecciona y copia el prompt de abajo.',
      assemblesPrefix: 'Reúne el estilo de la casa, ',
      assemblesAuthorPersonaClause: ({ authorName }: { authorName: string }) =>
        `la persona de ${authorName}`,
      assemblesNoAuthorClause: 'una nota de que no hay autor seleccionado',
      assemblesSuffix:
        ', las convenciones de Markdown de arriba, y una estructura solicitada — pégalo en un modelo externo, y luego pega el borrador de vuelta en el cuerpo.',

      aiPromptPersonaWithVoice: ({ authorName, persona }: { authorName: string; persona: string }) =>
        `Escribe con la voz de ${authorName}:\n${persona}`,
      aiPromptPersonaNoPersona: ({ authorName }: { authorName: string }) =>
        `${authorName} no tiene una persona configurada — escribe con una voz simple y directa.`,
      aiPromptAuthorFallbackLower: 'este autor',
      aiPromptAuthorFallbackUpper: 'Este autor',
      aiPromptTemplate: ({ personaSection }: { personaSection: string }) =>
        `Estás redactando una entrada de blog para Embent (embent.com), una plataforma de agentes de IA para sitios web.

Estilo de la casa:
- Directo y concreto. Sin hype, sin relleno de marketing.
- Párrafos cortos (2-4 frases).
- Nunca inventes estadísticas ni nombres de clientes. Si no tienes un número real o un cliente real, no lo inventes.

Voz del autor:
${personaSection}

Convenciones de Markdown (esto es TODO lo que se va a renderizar — todo lo demás se elimina):
- Encabezados (#, ##, ###), listas con viñetas y numeradas, citas en bloque.
- Enlaces como [texto](url) — solo http(s) y mailto.
- Imágenes en cualquier parte del cuerpo, incluido a mitad del artículo, como ![alt](url).
- Bloques de código en línea y con formato.
- Tablas (estilo GitHub).
- Las etiquetas HTML sin procesar y <script>/<style> se eliminan por completo — escribe solo Markdown simple, sin HTML.

Estructura solicitada:
1. Título
2. Gancho de un párrafo
3. 4-7 secciones, cada una con un encabezado h2 (##)
4. Un cierre breve

Escribe el borrador completo ahora en Markdown.`,
    },
    feedback: {
      filterLabel: {
        unhandled: 'Sin gestionar',
        all: 'Todos',
      },
      typeLabel: {
        bug: 'Error',
        feature: 'Función',
        question: 'Pregunta',
      },
      mailtoSubject: ({ id, type }: { id: string; type: string }) =>
        `Re: [Feedback de Embent ${id}] ${type}`,
      replyCta: 'Responder',
      colDate: 'Fecha',
      colWorkspace: 'Workspace',
      colUser: 'Usuario',
      colType: 'Tipo',
      colMessage: 'Mensaje',
      colActions: 'Acciones',
      emptyUnhandled: 'No hay feedback sin gestionar — bandeja en cero.',
      emptyAll: 'Todavía no hay feedback.',
      updateFeedbackErrorFallback: 'No se pudo actualizar este feedback.',
      reopeningCta: 'Reabriendo…',
      markingCta: 'Marcando…',
      reopenCta: 'Reabrir',
      markHandledCta: 'Marcar como gestionado',
      noBotFallback: 'sin bot',
      noUaFallback: 'sin UA',
    },
    playground: {
      demosAllTimeLabel: 'Demos de siempre',
      demos30dLabel: 'Demos · 30d',
      messagesAllTimeLabel: 'Mensajes de siempre',
      signedUpAllTimeLabel: 'Registrados',
      signedUp30dLabel: 'Registrados · 30d',
      leadsAllTimeLabel: 'Leads capturados',
      leads30dLabel: 'Leads · 30d',
      demosPerDayLabel: 'Demos por día',
      messagesPerDayLabel: 'Mensajes por día',
      demoUnit: ({ count }: { count: number }): string => (count === 1 ? 'demo' : 'demos'),
      messageUnit: ({ count }: { count: number }): string => (count === 1 ? 'mensaje' : 'mensajes'),
      noDemos30d: 'No hay demos en los últimos 30 días.',
      statusBreakdownHeading: 'Desglose de estado (30d)',
      recentDomainsHeading: 'Dominios recientes',
      noDemosYet: 'Todavía no hay demos.',
      colHostname: 'Hostname',
      colStatus: 'Estado',
      colPages: 'Páginas',
      colMessages: 'Mensajes',
      colAttempts: 'Intentos',
      colSignedUp: 'Registrado',
      colLeads: 'Leads',
      colCreated: 'Creado',
      signedUpAriaLabel: 'registrado',
      notSignedUpAriaLabel: 'no registrado',
      noEmailFallback: '(sin email)',
    },
  },
} satisfies Catalog;
